import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {useProducts} from '@amblique/king-living/app/hooks/use-products';
import {useAddItemToNewOrExistingBasket} from '@amblique/king-living/app/hooks/use-add-item-to-new-or-existing-basket';
import priceAriaMessages from '@salesforce/retail-react-app/app/components/display-price/messages';
import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image';
import Link from '@salesforce/retail-react-app/app/components/link';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  useBreakpointValue,
  useDisclosure,
  useMultiStyleConfig,
  useToast,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {useCurrency} from '@salesforce/retail-react-app/app/hooks';

import {RichTextContent} from '@amblique/contentful/app/components/rich-text-content';
import {getProductUrl} from '@amblique/king-living/app/components/want-it-sooner';
import {GET_PRODUCTS_PARAMS} from '@amblique/king-living/app/utils/api-utils';
import {formatCurrencyHideTrailingZeroCents} from '@amblique/king-living/app/utils/format-currency-hide-zero-cents';
import {getPrimaryCoverSwatchDisplay} from '@amblique/king-living/app/utils/product-cover-swatches';
import {getMasterTileImages} from '@amblique/king-living/app/utils/product-images';
import {buildDynamicImageSrc, buildSwatchSrc} from '@amblique/king-living/app/utils/responsive-image-src';
import ShopButton from '@amblique/king-living/app/components/shop-button';
import {
  ChevronRightIcon2,
  PlusIcon,
  ShopTheLookAddBagIcon,
  ShopTheLookFlyoutCloseIcon,
  ShopTheLookHotspotContentAddIcon,
  ShopTheLookHotspotContentSubIcon,
  ToggleChevronDownIcon,
  ToggleChevronUpIcon,
} from '@amblique/king-living/app/components/icons';
import {ShopTheLookHotspotPanel} from '@amblique/king-living/app/components/shop-the-look/shop-the-look-hotspot-panel';
import {
  getHotspotPositionPercent,
  hotspotHasCoordinates,
  isDefaultCenterHotspot,
  isEntryHotspot,
  isPageUrlHotspot,
  isProductHotspot,
  parseShopTheLookPayload,
} from '@amblique/king-living/app/utils/parse-shop-the-look-hotspots';
import {
  getProductEffectivePrice,
  getProductListPrice,
} from '@amblique/king-living/app/utils/product-utils';
import {Markup} from '@amblique/king-living/app/components/shared/markup';
import {useVisualViewportRect} from '@amblique/king-living/app/hooks/use-visual-viewport-rect';
import {setShopTheLookDrawerDocumentOpen} from '@amblique/king-living/app/hooks/use-shop-the-look-drawer-open';

const DRAWER_PLACEMENT = {base: 'bottom', md: 'right'};
const HOTSPOT_IMG_WIDTHS = ['64px', '80px', '120px'];

/** White plus on URL/product hotspots (Figma KL Designs). */
const HOTSPOT_PLUS_ICON_PROPS = {color: 'white', boxSize: '10px'};

/** [skidding, distance] per Chakra usePopper: 8px left + same vertical gap as former `gutter={10}`. */
const SHOP_THE_LOOK_HOTSPOT_POPOVER_OFFSET = [-8, 10];

/** Higher specificity (`&&`) replaces Chakra Popover defaults without !important */
const SHOP_THE_LOOK_POPOVER_CONTENT_SX = {
  'outline': 'none',
  '&&': {
    bg: 'transparent',
    border: 'none',
    boxShadow: 'none',
    filter: 'none',
    zIndex: 20,
  },
  '&&:focus': {boxShadow: 'none', outline: 'none'},
  '&&:focus-visible': {boxShadow: 'none', outline: 'none'},
  '&&:hover': {boxShadow: 'none'},
  '&&[data-state]': {boxShadow: 'none'},
  '&&[data-open]': {boxShadow: 'none'},
};

const SHOP_THE_LOOK_POPOVER_BODY_SX = {
  '&&': {
    boxShadow: 'none',
    outline: 'none',
  },
};

const SHOP_THE_LOOK_MARKETING_HEADING_COLOR = '#c6b5a1';

/** Marketing column: body #ddd, headings — sx beats global .amb-markup + Chakra Text color props. */
const SHOP_THE_LOOK_MARKETING_TEXT_SX = {
  '& .rich-text-content.amb-markup *': {
    color: '#dddddd !important',
  },
  '& .rich-text-content.amb-markup h1, & .rich-text-content.amb-markup h2, & .rich-text-content.amb-markup h3, & .rich-text-content.amb-markup h4, & .rich-text-content.amb-markup h5, & .rich-text-content.amb-markup h6': {
    color: `${SHOP_THE_LOOK_MARKETING_HEADING_COLOR} !important`,
  },
  '& .rich-text-content.amb-markup h1 *, & .rich-text-content.amb-markup h2 *, & .rich-text-content.amb-markup h3 *, & .rich-text-content.amb-markup h4 *, & .rich-text-content.amb-markup h5 *, & .rich-text-content.amb-markup h6 *': {
    color: `${SHOP_THE_LOOK_MARKETING_HEADING_COLOR} !important`,
  },
};

/**
 * Hotspot popover / drawer thumbnail. Aligned with PLP tiles & PDP hero (KINDW-1486):
 * delegate to `getMasterTileImages` so we honour the King Living PIM-first priority
 * (`c_processedImageGroups`) with a fallback to standard SCAPI `imageGroups`, then to the
 * top-level `product.image` carried by productSearch hits. Returning either `disBaseLink`
 * or `link` keeps Cylindo-only and PIM-only catalog entries from rendering a blank tile.
 */
function getTeaserImage(product) {
  if (!product) {
    return null;
  }

  const masterImage = getMasterTileImages(product).find(
    (img) => img.mediaType !== 'video' && (img.disBaseLink || img.link)
  );

  if (masterImage?.disBaseLink || masterImage?.link) {
    return masterImage;
  }

  if (product.image?.disBaseLink || product.image?.link) {
    return product.image;
  }

  return null;
}

function getTeaserImageSrc(image) {
  if (!image) {
    return null;
  }

  const raw = image.disBaseLink || image.link;

  return raw ? buildDynamicImageSrc(raw) : null;
}

function buildPdpPath(product) {
  if (!product) {
    return null;
  }

  return getProductUrl(product);
}

function buildPriceData(product) {
  const prices = product?.prices ?? {};
  let salePrice;

  const rawProcessed = product?.c_priceProcessed;

  if (rawProcessed) {
    try {
      const parsed = typeof rawProcessed === 'string' ? JSON.parse(rawProcessed) : rawProcessed;

      if (parsed != null && typeof parsed.salePrice === 'number') {
        salePrice = parsed.salePrice;
      }
    } catch {
      // ignore malformed c_priceProcessed
    }
  }

  if (salePrice == null) {
    salePrice = getProductEffectivePrice(product) ?? prices.salePrice ?? product?.price;
  }

  const listFromProcessed = getProductListPrice(product);
  const listFromApi =
    prices.listPrice != null && salePrice != null && prices.listPrice > salePrice ? prices.listPrice : null;
  const listPrice = listFromProcessed ?? listFromApi;
  const hasDiscount = listPrice != null && salePrice != null && listPrice > salePrice;

  return {
    currentPrice: salePrice ?? 0,
    listPrice: hasDiscount ? listPrice : null,
    isOnSale: hasDiscount,
    isASet: false,
    isMaster: false,
    isRange: false,
  };
}

/** Whole-dollar amounts omit trailing .00; cents still show (min 0, max 2 fraction digits). */
function formatShopTheLookDrawerCurrency(amount, currency, intl) {
  return formatCurrencyHideTrailingZeroCents(intl, amount, currency);
}

/** One-line spec under the title (plain text from short description when present). */
function getProductSpecLine(product) {
  const raw = product?.shortDescription;

  if (!raw || typeof raw !== 'string') {
    return null;
  }

  const plain = raw.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

  if (!plain) {
    return null;
  }

  return plain.length > 100 ? `${plain.slice(0, 97)}…` : plain;
}

/**
 * JSON hotspot CTA: use custom popover label and href only when both fields are set.
 *
 * @param {Record<string, unknown>} hotspot
 * @returns {boolean}
 */
function hotspotHasCtaOverride(hotspot) {
  const text = hotspot?.CTAText ?? hotspot?.ctaText;
  const link = hotspot?.CTALink ?? hotspot?.ctaLink;

  if (text == null || link == null) {
    return false;
  }

  return String(text).trim().length > 0 && String(link).trim().length > 0;
}

/** Merchant opt-in per hotspot JSON entry; missing/false defaults to ineligible. */
function isHotspotAddToBagEligible(hotspot) {
  return hotspot?.addToBag === true;
}

/**
 * Contentful-driven Shop the Look: single hero image, hotspots, product teaser, drawer with ATC.
 *
 * @param {Object} props
 * @param {Object} props.component - ShopTheLook entry fields from Contentful
 * @param {string} [props.variant] - Theme variant (e.g. blogTile for blog cards — no outer padding)
 */
export function ShopTheLookBlock({component, isFetching = false, variant, ...rest}) {
  const styles = useMultiStyleConfig('ShopTheLookBlock', {variant});
  const intl = useIntl();
  const toast = useToast();
  const {currency} = useCurrency();
  const popoverTrigger = useBreakpointValue({base: 'click', md: 'hover'});
  const drawerPlacement = useBreakpointValue(DRAWER_PLACEMENT);
  const shopTheLookDrawerSize = useBreakpointValue({base: 'full', md: '480px'});
  const isMobileDrawer = drawerPlacement === 'bottom';
  const preferMobileHotspotCoords = useBreakpointValue({base: true, md: false}) === true;

  const {
    isOpen: isProductDrawerOpen,
    onOpen: onProductDrawerOpen,
    onClose: onProductDrawerClose,
  } = useDisclosure();

  const drawerViewportRect = useVisualViewportRect(Boolean(isProductDrawerOpen && isMobileDrawer));

  useEffect(() => {
    setShopTheLookDrawerDocumentOpen(isProductDrawerOpen);

    return () => setShopTheLookDrawerDocumentOpen(false);
  }, [isProductDrawerOpen]);

  const [isHotspotPanelOpen, setIsHotspotPanelOpen] = React.useState(false);
  const [contentPanelIndex, setContentPanelIndex] = React.useState(0);
  const [hotspotPointerEventsLock, setHotspotPointerEventsLock] = React.useState(false);
  const hotspotLockTimeoutRef = useRef(null);
  const panelOpen = isProductDrawerOpen || isHotspotPanelOpen;
  const prevPanelOpenRef = useRef(panelOpen);

  const imageUrl = String(component?.imageUrl ?? '').trim();
  const mobileImageUrlField = String(component?.mobileImageUrl ?? '').trim();
  const desktopHeroSrc = imageUrl || mobileImageUrlField;
  const mobileHeroSrc = mobileImageUrlField || imageUrl;
  const hotspotsRaw = component?.hotspots;
  const {hotspots, quickAddOnly: quickAddOnlyFromJson} = useMemo(
    () => parseShopTheLookPayload(hotspotsRaw),
    [hotspotsRaw]
  );

  const contentItems = useMemo(() => {
    const coll = component?.hotspotsContentCollection?.items ?? [];

    return coll.filter(Boolean);
  }, [component?.hotspotsContentCollection?.items]);

  const safeContentIndex = useMemo(() => {
    if (!contentItems.length) {
      return 0;
    }

    return Math.min(Math.max(0, contentPanelIndex), contentItems.length - 1);
  }, [contentItems.length, contentPanelIndex]);

  const activeContentItem = contentItems[safeContentIndex] ?? null;

  const shopButtonLabel = useMemo(() => {
    const raw = component?.shopTheLookButtonLabel;

    if (raw != null && String(raw).trim() !== '') {
      return String(raw).trim();
    }

    return intl.formatMessage({
      defaultMessage: 'Shop the look',
      id: 'shop_the_look.button.label',
    });
  }, [component?.shopTheLookButtonLabel, intl]);

  const showShopButtonLabelDesktop = component?.showShopButtonLabelDesktop ?? true;
  const showShopButtonLabelMobile = component?.showShopButtonLabelMobile ?? false;
  const showShopTheLookButton = component?.showShopTheLookButton ?? true;

  const productIds = useMemo(() => {
    const ids = hotspots.map((h) => h.productId).filter(Boolean).map(String);

    return [...new Set(ids)].join(',');
  }, [hotspots]);

  const {data: productsMap, isLoading: isProductsLoading} = useProducts(
    {
      parameters: {
        ...GET_PRODUCTS_PARAMS,
        ids: productIds,
        expand: [...GET_PRODUCTS_PARAMS.expand, 'variations'],
      },
    },
    {
      enabled: Boolean(productIds),
      select: (result) => result?.data?.reduce((acc, item) => {
        acc[item.id] = item;

        return acc;
      }, {}) ?? {},
    }
  );

  const {addItemToNewOrExistingBasket} = useAddItemToNewOrExistingBasket();

  const productHotspots = useMemo(
    () => hotspots.filter((h) => isProductHotspot(h)),
    [hotspots]
  );

  const hasProductHotspots = productHotspots.length > 0;

  const allProductHotspotsAddToBagEligible = useMemo(
    () => productHotspots.length > 0 && productHotspots.every(isHotspotAddToBagEligible),
    [productHotspots]
  );

  /**
   * FSD: single product, no coordinate pins — icon only, one-click add, no popover/drawer flyout.
   * `quickAddOnly` in JSON root forces this even if an editor injected default 50/50 coords.
   */
  const isQuickAddOnly = useMemo(() => {
    if (hotspots.length !== 1 || productHotspots.length !== 1) {
      return false;
    }

    const h = hotspots[0];

    if (!isProductHotspot(h)) {
      return false;
    }

    if (quickAddOnlyFromJson) {
      return true;
    }

    if (!hotspotHasCoordinates(h)) {
      return true;
    }

    if (isDefaultCenterHotspot(h)) {
      return true;
    }

    return false;
  }, [hotspots, productHotspots, quickAddOnlyFromJson]);

  const openProductDrawer = useCallback(() => {
    onProductDrawerOpen();
  }, [onProductDrawerOpen]);

  const openHotspotPanel = useCallback(
    (index) => {
      const n = contentItems.length;

      if (!n) {
        return;
      }

      const clamped = Math.min(Math.max(0, index), n - 1);

      setContentPanelIndex(clamped);
      setIsHotspotPanelOpen(true);
    },
    [contentItems.length]
  );

  const closeHotspotPanel = useCallback(() => {
    setIsHotspotPanelOpen(false);
  }, []);

  const goNextHotspotContent = useCallback(() => {
    const n = contentItems.length;

    if (n < 2) {
      return;
    }

    setContentPanelIndex((i) => (i + 1) % n);
  }, [contentItems.length]);

  const goPrevHotspotContent = useCallback(() => {
    const n = contentItems.length;

    if (n < 2) {
      return;
    }

    setContentPanelIndex((i) => (i - 1 + n) % n);
  }, [contentItems.length]);

  useEffect(() => {
    if (panelOpen) {
      document.activeElement?.blur?.();
    }
  }, [panelOpen]);

  useEffect(() => {
    if (prevPanelOpenRef.current === true && panelOpen === false) {
      if (hotspotLockTimeoutRef.current != null) {
        window.clearTimeout(hotspotLockTimeoutRef.current);
      }
      setHotspotPointerEventsLock(true);
      hotspotLockTimeoutRef.current = window.setTimeout(() => {
        setHotspotPointerEventsLock(false);
        hotspotLockTimeoutRef.current = null;
      }, 450);
    }
    prevPanelOpenRef.current = panelOpen;
  }, [panelOpen]);

  useEffect(
    () => () => {
      if (hotspotLockTimeoutRef.current != null) {
        window.clearTimeout(hotspotLockTimeoutRef.current);
      }
    },
    []
  );

  const handleAddLine = useCallback(
    async (product) => {
      if (!product) {
        return;
      }

      try {
        await addItemToNewOrExistingBasket([
          {
            productId: product.id,
            price: product.price,
            quantity: 1,
          }
        ]);
        toast({
          title: intl.formatMessage({
            defaultMessage: 'Added to bag',
            id: 'shop_the_look.toast.added',
          }),
          status: 'success',
        });
      } catch (e) {
        toast({
          title: intl.formatMessage({
            defaultMessage: 'Could not add to bag',
            id: 'shop_the_look.toast.error',
          }),
          status: 'error',
        });
      }
    },
    [addItemToNewOrExistingBasket, intl, toast]
  );

  const handleAddAll = useCallback(async () => {
    const items = [];

    for (const h of productHotspots) {
      if (!isHotspotAddToBagEligible(h)) {
        continue;
      }

      const p = productsMap?.[h.productId];

      if (p) {
        items.push({
          productId: p.id,
          price: p.price,
          quantity: 1,
        });
      }
    }

    if (!items.length) {
      toast({
        title: intl.formatMessage({
          defaultMessage: 'No eligible products to add',
          id: 'shop_the_look.toast.no_eligible',
        }),
        status: 'info',
      });

      return;
    }

    try {
      await addItemToNewOrExistingBasket(items);
      toast({
        title: intl.formatMessage({
          defaultMessage: 'Added to bag',
          id: 'shop_the_look.toast.added',
        }),
        status: 'success',
      });
      onProductDrawerClose();
    } catch {
      toast({
        title: intl.formatMessage({
          defaultMessage: 'Some items could not be added',
          id: 'shop_the_look.toast.partial',
        }),
        status: 'warning',
      });
    }
  }, [addItemToNewOrExistingBasket, intl, onProductDrawerClose, productHotspots, productsMap, toast]);

  const handleQuickAdd = useCallback(async () => {
    const h = hotspots[0];

    if (!h?.productId) {
      return;
    }

    if (isProductsLoading) {
      toast({
        title: intl.formatMessage({
          defaultMessage: 'Loading product…',
          id: 'shop_the_look.toast.loading_product',
        }),
        status: 'info',
      });

      return;
    }

    const p = productsMap?.[h.productId];

    if (!p) {
      toast({
        title: intl.formatMessage({
          defaultMessage: 'Product could not be loaded. Check the product ID in Contentful.',
          id: 'shop_the_look.toast.product_missing',
        }),
        status: 'warning',
      });

      return;
    }

    await handleAddLine(p);
  }, [handleAddLine, hotspots, intl, isProductsLoading, productsMap, toast]);

  const renderHotspot = (hotspot, index) => {
    const pos = getHotspotPositionPercent(hotspot, {preferMobile: preferMobileHotspotCoords});

    if (isPageUrlHotspot(hotspot)) {
      const external = hotspot.newTab !== false;

      return (
        <Box key={`url-${index}`} {...styles.hotspotAnchor} left={pos.left} top={pos.top} zIndex={2}>
          <Link href={String(hotspot.pageUrl)} isExternal={external}>
            <Box {...styles.hotspotDot} as="span">
              <PlusIcon {...HOTSPOT_PLUS_ICON_PROPS} />
            </Box>
          </Link>
        </Box>
      );
    }

    if (isEntryHotspot(hotspot)) {
      const idx = parseInt(String(hotspot.entryId), 10);
      const entryContentIndex = Number.isFinite(idx) ? idx : 0;
      const isContentHotspotActive =
        isHotspotPanelOpen && entryContentIndex === safeContentIndex;
      let contentHotspotAriaLabel = intl.formatMessage({
        defaultMessage: 'Open hotspot content',
        id: 'shop_the_look.a11y.content_hotspot',
      });

      if (isContentHotspotActive) {
        contentHotspotAriaLabel = intl.formatMessage({
          defaultMessage: 'Close hotspot content',
          id: 'shop_the_look.a11y.close_content_hotspot',
        });
      }

      return (
        <Box
          key={`entry-${index}`}
          {...styles.hotspotAnchor}
          left={pos.left}
          top={pos.top}
          zIndex={2}
        >
          <Box
            {...styles.hotspotContentDot}
            aria-label={contentHotspotAriaLabel}
            as="button"
            type="button"
            onClick={() => {
              if (isContentHotspotActive) {
                closeHotspotPanel();
              } else {
                openHotspotPanel(entryContentIndex);
              }
            }}
          >
            {isContentHotspotActive ? (
              <ShopTheLookHotspotContentSubIcon boxSize="100%" aria-hidden />
            ) : (
              <ShopTheLookHotspotContentAddIcon boxSize="100%" aria-hidden />
            )}
          </Box>
        </Box>
      );
    }

    if (isProductHotspot(hotspot)) {
      const product = productsMap?.[hotspot.productId];
      const useCtaOverride = hotspotHasCtaOverride(hotspot);
      const ctaLabel = useCtaOverride ? String(hotspot.CTAText ?? hotspot.ctaText).trim() : '';
      const ctaHref = useCtaOverride ? String(hotspot.CTALink ?? hotspot.ctaLink).trim() : '';
      const img = product ? getTeaserImage(product) : null;
      const imgSrc = getTeaserImageSrc(img);
      const pdp = product ? buildPdpPath(product) : null;
      const newTab = hotspot.newTab !== false;
      const coverLabel = product?.shortDescription ?? '';

      return (
        <Box key={`p-${hotspot.productId}-${index}`} {...styles.hotspotAnchor} left={pos.left} top={pos.top} zIndex={2}>
          <Popover
            offset={SHOP_THE_LOOK_HOTSPOT_POPOVER_OFFSET}
            openDelay={popoverTrigger === 'hover' ? 100 : 0}
            placement="top-start"
            strategy="fixed"
            trigger={popoverTrigger}
          >
            <PopoverTrigger>
              <Box
                {...styles.hotspotDot}
                as="button"
                type="button"
                aria-label={intl.formatMessage({
                  defaultMessage: 'Product hotspot',
                  id: 'shop_the_look.a11y.product_hotspot',
                })}
              >
                <PlusIcon {...HOTSPOT_PLUS_ICON_PROPS} />
              </Box>
            </PopoverTrigger>
            <Portal>
              <PopoverContent
                {...styles.hotspotPopoverContent}
                shadow="none"
                sx={SHOP_THE_LOOK_POPOVER_CONTENT_SX}
              >
                <PopoverBody {...styles.hotspotPopoverBody} sx={SHOP_THE_LOOK_POPOVER_BODY_SX}>
                  {useCtaOverride ? (
                    <Box {...styles.hotspotPopoverCard}>
                      <Link href={ctaHref} isExternal={newTab} {...styles.hotspotPopoverLink}>
                        <Box {...styles.hotspotPopoverTextCol}>
                          <Text {...styles.hotspotPopoverTitle}>{ctaLabel}</Text>
                        </Box>
                        <ChevronRightIcon2 {...styles.hotspotPopoverChevron} boxSize={5} aria-hidden />
                      </Link>
                    </Box>
                  ) : isProductsLoading && !product ? (
                    <Box {...styles.hotspotPopoverCard}>
                      <Text fontSize="sm" p={4}>
                        …
                      </Text>
                    </Box>
                  ) : product ? (
                    <Box {...styles.hotspotPopoverCard}>
                      {pdp ? (
                        <Link href={pdp} isExternal={newTab} {...styles.hotspotPopoverLink}>
                          <Box {...styles.hotspotPopoverThumb}>
                            {imgSrc ? (
                              <DynamicImage
                                src={imgSrc}
                                widths={HOTSPOT_IMG_WIDTHS}
                                imageProps={{
                                  alt: img?.alt || product.name || '',
                                  style: {width: '100%', height: '100%', objectFit: 'cover'},
                                }}
                              />
                            ) : null}
                          </Box>
                          <Box {...styles.hotspotPopoverTextCol}>
                            <Text {...styles.hotspotPopoverTitle}>{product.name}</Text>
                            {coverLabel ? <Markup source={coverLabel} {...styles.hotspotPopoverMeta} /> : null}
                          </Box>
                          <ChevronRightIcon2 {...styles.hotspotPopoverChevron} boxSize={5} aria-hidden />
                        </Link>
                      ) : (
                        <Box {...styles.hotspotPopoverLink} cursor="default">
                          <Box {...styles.hotspotPopoverThumb}>
                            {imgSrc ? (
                              <DynamicImage
                                src={imgSrc}
                                widths={HOTSPOT_IMG_WIDTHS}
                                imageProps={{
                                  alt: img?.alt || product.name || '',
                                  style: {width: '100%', height: '100%', objectFit: 'cover'},
                                }}
                              />
                            ) : null}
                          </Box>
                          <Box {...styles.hotspotPopoverTextCol}>
                            <Text {...styles.hotspotPopoverTitle}>{product.name}</Text>
                            {coverLabel ? <Markup source={coverLabel} {...styles.hotspotPopoverMeta} /> : null}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box {...styles.hotspotPopoverCard}>
                      <Text color="gray.600" fontSize="sm" p={4}>
                        <FormattedMessage
                          defaultMessage="Product unavailable"
                          id="shop_the_look.unavailable"
                        />
                      </Text>
                    </Box>
                  )}
                </PopoverBody>
              </PopoverContent>
            </Portal>
          </Popover>
        </Box>
      );
    }

    return null;
  };

  const marketingHeading =
    typeof component?.heading === 'string' ? component.heading.trim() : '';
  const hasMarketingHeading = Boolean(marketingHeading);
  // ShopTheLook rich text is queried as shopTheLookRichText (alias; avoids union field clash with Link.text).
  const marketingRichText = component?.shopTheLookRichText ?? component?.text ?? null;
  const hasMarketingBody = Boolean(marketingRichText?.json);
  const hasMarketingContent = hasMarketingHeading || hasMarketingBody;

  const shopTheLookLayerJustifyMd = useMemo(
    () => (hasMarketingContent && !isHotspotPanelOpen ? 'center' : 'flex-start'),
    [hasMarketingContent, isHotspotPanelOpen]
  );

  const shopTheLookLayerGapMd = useMemo(
    () => (hasMarketingContent && !isHotspotPanelOpen ? 10 : 0),
    [hasMarketingContent, isHotspotPanelOpen]
  );

  const shopTheLookLayerContainerMinHMd = useMemo(
    () => (isHotspotPanelOpen && hasMarketingContent ? 'min(620px, 90vh)' : undefined),
    [hasMarketingContent, isHotspotPanelOpen]
  );

  const imageColumnFlex = useMemo(() => {
    let md = '1 1 100%';

    if (isHotspotPanelOpen) {
      // Overlay panel does not shrink the row; keep the same hero width as when marketing is shown (closed) so the image does not re-flow wider ("zoom").
      md = hasMarketingContent ? '0 0 66%' : '1 1 100%';
    } else if (hasMarketingContent) {
      // Image column ~65–67% of the 1120px layer; text stays ~24rem max.
      md = '1 1 66%';
    }

    return {base: 'none', md};
  }, [hasMarketingContent, isHotspotPanelOpen]);

  const imageColumnMaxW = useMemo(() => {
    let md = '100%';

    if (isHotspotPanelOpen) {
      md = hasMarketingContent ? '68%' : '100%';
    } else if (hasMarketingContent) {
      md = '68%';
    }

    return {md};
  }, [hasMarketingContent, isHotspotPanelOpen]);

  const layerFlexMinHMd = useMemo(() => {
    if (isHotspotPanelOpen && hasMarketingContent && shopTheLookLayerContainerMinHMd) {
      return shopTheLookLayerContainerMinHMd;
    }

    return undefined;
  }, [hasMarketingContent, isHotspotPanelOpen, shopTheLookLayerContainerMinHMd]);

  const suppressMainHeroPointerEvents =
    Boolean(isProductDrawerOpen && drawerPlacement === 'bottom' && !isQuickAddOnly);

  if (!desktopHeroSrc) {
    return null;
  }

  return (
    <Box
      data-testid="shop-the-look-block"
      {...styles.root}
      {...(isHotspotPanelOpen ? styles.rootWhenHotspotPanelOpen : {})}
      {...rest}
    >
      <Box
        minH={{md: shopTheLookLayerContainerMinHMd}}
        {...(hasMarketingContent ? styles.layerContainerWithMarketing : {})}
        {...styles.layerContainer}
        className="shop-the-look-block__layer-container"
      >
        <Flex
          {...styles.layerFlex}
          align={hasMarketingContent && !isHotspotPanelOpen ? 'center' : 'stretch'}
          className="shop-the-look-block__layer-flex"
          direction={{base: 'column', md: 'row'}}
          gap={{base: 6, md: shopTheLookLayerGapMd}}
          justify={{md: shopTheLookLayerJustifyMd}}
          minH={{md: layerFlexMinHMd}}
          width="full"
        >
          {hasMarketingHeading ? (
            <Box display={{base: 'block', md: 'none'}} px={{base: 4, md: 0}} textAlign="center" w="full">
              <Text
                as="h2"
                color={SHOP_THE_LOOK_MARKETING_HEADING_COLOR}
                fontSize={{base: 'xl', md: '2xl'}}
                fontWeight="normal"
                lineHeight="tall"
              >
                {marketingHeading}
              </Text>
            </Box>
          ) : null}

          <Box
            {...styles.imageColumn}
            {...(isHotspotPanelOpen ? styles.imageColumnWhenHotspotPanelOpen : {})}
            {...(isHotspotPanelOpen ? {flexShrink: {md: 0}} : {})}
            className="shop-the-look-block__image-column"
            flex={imageColumnFlex}
            maxW={imageColumnMaxW}
            minW={0}
            pointerEvents={suppressMainHeroPointerEvents ? 'none' : undefined}
          >
            <Box
              {...styles.imageWrap}
              {...(isHotspotPanelOpen ? styles.imageWrapWhenHotspotPanelOpen : {})}
            >
              {desktopHeroSrc === mobileHeroSrc ? (
                <Box
                  alt={component?.title ? String(component.title) : 'Shop the look'}
                  as="img"
                  loading="lazy"
                  src={desktopHeroSrc}
                  {...styles.image}
                />
              ) : (
                <>
                  <Box
                    alt={component?.title ? String(component.title) : 'Shop the look'}
                    as="img"
                    loading="lazy"
                    src={mobileHeroSrc}
                    {...styles.image}
                    display={{base: 'block', md: 'none'}}
                  />
                  <Box
                    alt={component?.title ? String(component.title) : 'Shop the look'}
                    as="img"
                    loading="lazy"
                    src={desktopHeroSrc}
                    {...styles.image}
                    display={{base: 'none', md: 'block'}}
                  />
                </>
              )}

              {!isQuickAddOnly ? (
                <Box
                  bottom={0}
                  left={0}
                  pointerEvents={hotspotPointerEventsLock ? 'none' : 'auto'}
                  position="absolute"
                  right={0}
                  top={0}
                  zIndex={1}
                >
                  {hotspots.map((h, i) => renderHotspot(h, i))}
                </Box>
              ) : null}

              {!isQuickAddOnly && hasProductHotspots && showShopTheLookButton ? (
                <Box {...styles.gradientBar}>
                  <Box {...styles.shopActions}>
                    <ShopButton
                      showLabelDesktop={showShopButtonLabelDesktop}
                      showLabelMobile={showShopButtonLabelMobile}
                      text={shopButtonLabel}
                      onClick={openProductDrawer}
                    />
                  </Box>
                </Box>
              ) : null}

              {isQuickAddOnly && showShopTheLookButton && isHotspotAddToBagEligible(hotspots[0]) ? (
                <Box {...styles.quickAddBtn}>
                  <ShopButton
                    showLabelDesktop={showShopButtonLabelDesktop}
                    showLabelMobile={showShopButtonLabelMobile}
                    text={shopButtonLabel}
                    onClick={handleQuickAdd}
                  />
                </Box>
              ) : null}
            </Box>
          </Box>

          {hasMarketingContent && !isHotspotPanelOpen ? (
            <Box
              {...styles.marketingTextColumn}
              display={{base: hasMarketingBody ? 'block' : 'none', md: 'block'}}
              sx={SHOP_THE_LOOK_MARKETING_TEXT_SX}
              textAlign={{base: 'center', md: 'left'}}
            >
              {hasMarketingHeading ? (
                <Text
                  as="h2"
                  color={SHOP_THE_LOOK_MARKETING_HEADING_COLOR}
                  display={{base: 'none', md: 'block'}}
                  fontSize={{md: '2xl'}}
                  fontWeight="normal"
                  lineHeight="tall"
                  mb={hasMarketingBody ? 4 : 0}
                >
                  {marketingHeading}
                </Text>
              ) : null}
              {hasMarketingBody ? <RichTextContent content={marketingRichText} /> : null}
            </Box>
          ) : null}
        </Flex>

        {isHotspotPanelOpen && activeContentItem ? (
          <Box {...styles.hotspotSidePanel}>
            <Text as="h2" srOnly>
              <FormattedMessage defaultMessage="Hotspot details" id="shop_the_look.drawer.title_hotspot" />
            </Text>
            <IconButton
              {...styles.hotspotPanelCloseBtn}
              icon={<ShopTheLookFlyoutCloseIcon boxSize="20px" aria-hidden />}
              variant="unstyled"
              aria-label={intl.formatMessage({
                defaultMessage: 'Close',
                id: 'shop_the_look.a11y.close_panel',
              })}
              onClick={closeHotspotPanel}
            />
            <Box
              display="flex"
              flex="1"
              flexDirection="column"
              h="100%"
              minH={0}
              minW={0}
              w="full"
            >
              <ShopTheLookHotspotPanel
                activeIndex={safeContentIndex}
                item={activeContentItem}
                items={contentItems}
                styles={styles}
              />
            </Box>
            {contentItems.length > 1 ? (
              <Flex {...styles.hotspotPanelNavStack}>
                <IconButton
                  {...styles.hotspotPanelNavBtn}
                  icon={<ToggleChevronUpIcon boxSize="20px" aria-hidden />}
                  variant="unstyled"
                  aria-label={intl.formatMessage({
                    defaultMessage: 'Previous hotspot',
                    id: 'shop_the_look.a11y.hotspot_prev',
                  })}
                  onClick={goPrevHotspotContent}
                />
                <IconButton
                  {...styles.hotspotPanelNavBtn}
                  icon={<ToggleChevronDownIcon boxSize="20px" aria-hidden />}
                  variant="unstyled"
                  aria-label={intl.formatMessage({
                    defaultMessage: 'Next hotspot',
                    id: 'shop_the_look.a11y.hotspot_next',
                  })}
                  onClick={goNextHotspotContent}
                />
              </Flex>
            ) : null}
          </Box>
        ) : null}
      </Box>

      <Drawer
        isOpen={isProductDrawerOpen}
        placement={drawerPlacement ?? 'right'}
        size={shopTheLookDrawerSize}
        onClose={onProductDrawerClose}
      >
        <DrawerOverlay {...styles.drawerOverlay} />
        <DrawerContent
          {...styles.drawerContent}
          {...(drawerViewportRect ?
            {
              bottom: 'auto',
              height: drawerViewportRect.height,
              left: drawerViewportRect.left,
              maxHeight: drawerViewportRect.height,
              position: 'fixed',
              right: 'auto',
              top: drawerViewportRect.top,
              width: drawerViewportRect.width,
            } :
            {})}
        >
          {isProductDrawerOpen && !isQuickAddOnly ? (
            <Box {...styles.drawerHeroWrap} display={{base: 'block', md: 'none'}}>
              <Box {...styles.drawerHeroMedia}>
                <Box
                  alt={component?.title ? String(component.title) : 'Shop the look'}
                  as="img"
                  loading="lazy"
                  src={mobileHeroSrc}
                  {...styles.drawerHeroImage}
                />
                <Box
                  bottom={0}
                  left={0}
                  pointerEvents={hotspotPointerEventsLock ? 'none' : 'auto'}
                  position="absolute"
                  right={0}
                  top={0}
                  zIndex={1}
                >
                  {hotspots.map((h, i) => renderHotspot(h, i))}
                </Box>
              </Box>
            </Box>
          ) : null}
          <DrawerHeader {...styles.drawerHeaderProducts}>
            <Flex {...styles.drawerProductsHeaderStack}>
              <Flex {...styles.drawerHeaderRow} justifyContent="flex-end">
                <DrawerCloseButton {...styles.drawerCloseButtonFlow}>
                  <ShopTheLookFlyoutCloseIcon boxSize="20px" aria-hidden />
                </DrawerCloseButton>
              </Flex>
              <Flex {...styles.drawerBodyTitleRow}>
                <Text as="h2" {...styles.drawerBodyTitle}>
                  <FormattedMessage defaultMessage="Shop the look" id="shop_the_look.drawer.title_products" />
                </Text>
                <Text as="span" {...styles.drawerItemCount}>
                  <FormattedMessage
                    defaultMessage="{count, plural, one {# Item} other {# Items}}"
                    id="shop_the_look.drawer.item_count"
                    values={{count: productHotspots.length}}
                  />
                </Text>
              </Flex>
            </Flex>
          </DrawerHeader>
          <DrawerBody
            display="flex"
            flex={1}
            flexDirection="column"
            minH={0}
            overflow="hidden"
            p={0}
            pl={0}
            pr={0}
            sx={styles.drawerBody}
          >
            <Box
              p={0}
              {...styles.drawerBodyProducts}
            >
              {productHotspots.map((h) => {
                const product = productsMap?.[h.productId];

                if (!product) {
                  return (
                    <Box key={h.productId} py={4}>
                      <Text color="gray.600" fontSize="sm">
                        {h.productId}
                      </Text>
                    </Box>
                  );
                }

                const img = getTeaserImage(product);
                const imgSrc = getTeaserImageSrc(img);
                const priceData = buildPriceData(product);
                const coverSwatch = getPrimaryCoverSwatchDisplay(product);
                const specLine = getProductSpecLine(product);
                const productCurrency = product.currency ?? currency ?? 'AUD';
                const pdpPath = buildPdpPath(product);
                const drawerTitleContent = pdpPath ? (
                  <Link href={pdpPath} {...styles.drawerProductTitleLink}>
                    {product.name}
                  </Link>
                ) : (
                  <Text {...styles.drawerProductTitle}>{product.name}</Text>
                );
                const drawerPriceFormatted = formatShopTheLookDrawerCurrency(
                  priceData.currentPrice,
                  productCurrency,
                  intl
                );

                return (
                  <Flex key={product.id} {...styles.drawerProductRow}>
                    {imgSrc ? (
                      <Box {...styles.drawerProductImage}>
                        <Box {...styles.drawerProductImageInner}>
                          <DynamicImage
                            src={imgSrc}
                            widths={['96px', '130px', '260px']}
                            imageProps={{
                              alt: img?.alt || product.name || '',
                              style: {width: '100%', height: '100%', objectFit: 'cover'},
                            }}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <Box {...styles.drawerProductImage} />
                    )}
                    <Box {...styles.drawerProductMain}>
                      <Text {...styles.drawerProductTitle}>
                        {drawerTitleContent}
                      </Text>
                      {specLine ? <Text {...styles.drawerProductSpec}>{specLine}</Text> : null}
                      {coverSwatch ? (
                        <Flex {...styles.drawerSwatchRow}>
                          <Box
                            {...styles.drawerSwatchDot}
                            backgroundPosition="center"
                            backgroundRepeat="no-repeat"
                            backgroundSize="cover"
                            bg={coverSwatch.swatchUrl ? undefined : 'gray.100'}
                            backgroundImage={
                              coverSwatch.swatchUrl ? `url(${buildSwatchSrc(coverSwatch.swatchUrl)})` : undefined
                            }
                            aria-hidden
                          />
                          <Text {...styles.drawerSwatchLabel}>{coverSwatch.label}</Text>
                        </Flex>
                      ) : null}
                      <Flex {...styles.drawerPriceRow}>
                        <Text
                          as="span"
                          {...styles.drawerCurrentPrice}
                          aria-label={intl.formatMessage(priceAriaMessages.ariaLabelCurrentPrice, {
                            currentPrice: drawerPriceFormatted,
                          })}
                        >
                          {drawerPriceFormatted}
                        </Text>
                        {priceData.isOnSale && priceData.listPrice != null ? (
                          <Text as="span" {...styles.drawerTotalValue}>
                            <FormattedMessage defaultMessage="Total value" id="shop_the_look.drawer.total_value_prefix" />
                            {' '}
                            <Text as="span" {...styles.drawerTotalValueStrikethrough}>
                              {formatCurrencyHideTrailingZeroCents(
                                intl,
                                priceData.listPrice,
                                productCurrency
                              )}
                            </Text>
                          </Text>
                        ) : null}
                      </Flex>
                    </Box>
                    {isHotspotAddToBagEligible(h) ? (
                      <Flex {...styles.drawerProductActions}>
                        <Button
                          {...styles.drawerIconButton}
                          aria-label={intl.formatMessage({defaultMessage: 'Add to bag', id: 'shop_the_look.a11y.add_to_bag'})}
                          onClick={() => handleAddLine(product)}
                        >
                          <ShopTheLookAddBagIcon boxSize="20px" aria-hidden />
                        </Button>
                      </Flex>
                    ) : null}
                  </Flex>
                );
              })}
            </Box>
          </DrawerBody>
          {allProductHotspotsAddToBagEligible ? (
            <DrawerFooter {...styles.drawerFooter}>
              <Button
                {...styles.drawerFooterBtnBag}
                leftIcon={<ShopTheLookAddBagIcon boxSize="18px" aria-hidden />}
                onClick={handleAddAll}
              >
                <FormattedMessage defaultMessage="Add all to bag" id="shop_the_look.add_all" />
              </Button>
            </DrawerFooter>
          ) : null}
        </DrawerContent>
      </Drawer>

    </Box>
  );
}

ShopTheLookBlock.propTypes = {
  component: PropTypes.object.isRequired,
  isFetching: PropTypes.bool,
  variant: PropTypes.string,
};
